import { JSX, useEffect, useLayoutEffect, useRef, useState } from 'react'
import './ToolTip.css'

export interface ToolTipProps {
	style: 'top' | 'left',
	position: { x: number, y: number },
	content: string | number | JSX.Element,
	isShown: boolean
}

const MARGIN = 8
const ARROW_SIZE = 30
const HIDE_DELAY = 80

export const ToolTip = ({ content, isShown, position, style }: ToolTipProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null)
	const [pos, setPos] = useState({ left: position.x + 30, top: position.y })
	const [arrowOffset, setArrowOffset] = useState(ARROW_SIZE / 2)
	const [side, setSide] = useState<'start' | 'end'>('start')
	const [rendered, setRendered] = useState(isShown)
	const [visible, setVisible] = useState(false)
	const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useLayoutEffect(() => {
		if (isShown) {
			if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
			if (!rendered) {
				setPos(style === 'top' ?
					{ left: position.x, top: position.y + 35 } :
					{ left: position.x + 25, top: position.y - 25 })
			}
			setRendered(true)
			return
		}
		hideTimeoutRef.current = setTimeout(() => setRendered(false), HIDE_DELAY)
		return () => {
			if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current)
		}
	}, [isShown])

	useEffect(() => {
		if (!rendered) {
			setVisible(false)
			return
		}
		const raf = requestAnimationFrame(() => setVisible(true))
		return () => cancelAnimationFrame(raf)
	}, [rendered])

	const updatePositionRef = useRef(() => { })
	updatePositionRef.current = () => {
		if (!wrapperRef.current) return
		const { width, height } = wrapperRef.current.getBoundingClientRect()

		let left: number
		let newSide: 'start' | 'end' = 'start'
		let top: number

		if (style === 'top') {
			left = position.x - width / 2
			top = position.y + 35
		} else {
			const rightLeft = position.x + 25
			const fitsRight = rightLeft + width <= window.innerWidth - MARGIN
			if (fitsRight) {
				left = rightLeft
				newSide = 'start'
			} else {
				left = position.x - 25 - width
				newSide = 'end'
			}
			top = position.y - 25
		}

		const clampedTop = Math.min(Math.max(top, MARGIN), window.innerHeight - height - MARGIN)
		const clampedLeft = Math.min(Math.max(left, MARGIN), window.innerWidth - width - MARGIN)
		setPos({ left: clampedLeft, top: clampedTop })
		setSide(newSide)

		if (style === 'left') {
			setArrowOffset(Math.min(Math.max(position.y - clampedTop - 15, ARROW_SIZE / 2), height - ARROW_SIZE / 2))
		}
	}

	useLayoutEffect(() => {
		if (!rendered) return
		updatePositionRef.current()
	}, [rendered, position.x, position.y, style, content])

	// re-clamp position when the tooltip's own content resizes (e.g. an inner
	// component expands), since that doesn't change any of the deps above
	useEffect(() => {
		if (!rendered || !wrapperRef.current) return
		const observer = new ResizeObserver(() => updatePositionRef.current())
		observer.observe(wrapperRef.current)
		return () => observer.disconnect()
	}, [rendered])

	if (!rendered) return null

	return (
		<div
			ref={wrapperRef}
			className={`tooltip-wrapper ${style} ${side} ${visible ? 'show' : ''}`}
			style={pos}
		>
			<div className={`tooltip ${style} ${side}`}>
				<div
					className={`tooltip-dialogue-arrow ${style} ${side}`}
					style={style === 'left' ? { alignSelf: 'flex-start', marginTop: arrowOffset - ARROW_SIZE / 2 } : undefined}
				/>
				<div className='tooltip-text'>
					{content}
				</div>
			</div>
		</div>
	)
}